// Inventario de farmacia interna (2026-09-11) — carga de lotes y ajustes,
// mismo motor que /admin/inventario (lib/billing.ts:createStockLot/
// adjustStockLot), alcanzable sin el login admin_users.
import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { notify } from '@/components/ui/sonner';
import {
  ajustarLoteFarmacia,
  cargarLoteFarmacia,
  listarCatalogoFarmacia,
  listarInventarioFarmacia,
  listarItemsElegiblesBotiquin,
  listarLotesTransferibles,
  sugerenciaReposicionBotiquin,
  transferirABotiquin,
  type CatalogoItemFarmacia,
  type ItemElegibleBotiquin,
  type ItemInventarioFarmacia,
  type LoteTransferible,
} from '@/lib/farmaciaInterna';

/** Formulario "Reponer botiquín de servicio" (Fase 2, ítem 12) — transferencia firmada al espejo del CRM. Componente aparte, autocontenido: su propio estado no interfiere con el de "Cargar lote" de arriba. */
function ReponerBotiquin({ onTransferido }: { onTransferido: () => void }): JSX.Element {
  const { t } = useTranslation();
  const [items, setItems] = useState<ItemElegibleBotiquin[]>([]);
  const [farmaciaItemId, setFarmaciaItemId] = useState('');
  const [lotes, setLotes] = useState<LoteTransferible[]>();
  const [lotId, setLotId] = useState('');
  const [sugerido, setSugerido] = useState<number | null>();
  const [quantity, setQuantity] = useState('');
  const [transfiriendo, setTransfiriendo] = useState(false);

  useEffect(() => {
    void listarItemsElegiblesBotiquin().then(setItems);
  }, []);

  useEffect(() => {
    setLotId('');
    setLotes(undefined);
    setSugerido(undefined);
    setQuantity('');
    if (!farmaciaItemId) return;
    const id = Number(farmaciaItemId);
    void listarLotesTransferibles(id).then(setLotes);
    void sugerenciaReposicionBotiquin(id).then((s) => {
      setSugerido(s.sugerido);
      if (s.sugerido && s.sugerido > 0) setQuantity(String(s.sugerido));
    });
  }, [farmaciaItemId]);

  const loteElegido = lotes?.find((l) => l.id === Number(lotId));

  async function transferir(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setTransfiriendo(true);
    try {
      const r = await transferirABotiquin({ farmaciaItemId: Number(farmaciaItemId), lotId: Number(lotId), quantity: Number(quantity) });
      if (!r.ok) {
        notify({ color: 'red', message: r.error, autoClose: false });
        return;
      }
      notify({ color: 'green', message: t('farmaciaApp.botiquin.exito', { cantidad: r.datos.quantity, lote: r.datos.espejoLotCode }) });
      setFarmaciaItemId('');
      setLotId('');
      setQuantity('');
      onTransferido();
    } finally {
      setTransfiriendo(false);
    }
  }

  return (
    <form onSubmit={(e) => void transferir(e)} className="space-y-3 rounded-lg border border-border p-4">
      <div>
        <p className="text-sm font-medium text-hce-ink">{t('farmaciaApp.botiquin.titulo')}</p>
        <p className="text-xs text-hce-muted">{t('farmaciaApp.botiquin.descripcion')}</p>
      </div>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <div className="col-span-2">
          <Label className="mb-1 block text-xs">{t('farmaciaApp.botiquin.item')}</Label>
          <Select value={farmaciaItemId} onValueChange={setFarmaciaItemId}>
            <SelectTrigger>
              <SelectValue placeholder={t('farmaciaApp.botiquin.itemPlaceholder')} />
            </SelectTrigger>
            <SelectContent>
              {items.map((item) => (
                <SelectItem key={item.farmaciaItemId} value={String(item.farmaciaItemId)}>
                  {item.nombre} — {item.disponible} {item.unidad}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="mb-1 block text-xs">{t('farmaciaApp.botiquin.lote')}</Label>
          <Select value={lotId} onValueChange={setLotId} disabled={!farmaciaItemId || (lotes?.length ?? 0) === 0}>
            <SelectTrigger>
              <SelectValue placeholder={t('farmaciaApp.botiquin.lotePlaceholder')} />
            </SelectTrigger>
            <SelectContent>
              {(lotes ?? []).map((l) => (
                <SelectItem key={l.id} value={String(l.id)}>
                  {l.lotCode} — {l.quantity}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {farmaciaItemId && lotes?.length === 0 && <p className="mt-1 text-xs text-destructive">{t('farmaciaApp.botiquin.sinLotes')}</p>}
        </div>
        <div>
          <Label className="mb-1 block text-xs">{t('farmaciaApp.botiquin.cantidad')}</Label>
          <Input type="number" min="1" max={loteElegido?.quantity} value={quantity} onChange={(e) => setQuantity(e.target.value)} required />
        </div>
      </div>
      {farmaciaItemId && sugerido !== undefined && (
        <p className="text-xs text-hce-muted">{sugerido && sugerido > 0 ? t('farmaciaApp.botiquin.sugerencia', { cantidad: sugerido }) : t('farmaciaApp.botiquin.sinSugerencia')}</p>
      )}
      <Button type="submit" disabled={transfiriendo || !farmaciaItemId || !lotId || !quantity}>
        {transfiriendo ? t('farmaciaApp.botiquin.transferiendo') : t('farmaciaApp.botiquin.transferir')}
      </Button>
    </form>
  );
}

export function InventarioFarmacia(): JSX.Element {
  const { t } = useTranslation();
  const [items, setItems] = useState<ItemInventarioFarmacia[]>();
  const [catalogo, setCatalogo] = useState<CatalogoItemFarmacia[]>([]);
  const [catalogItemId, setCatalogItemId] = useState('');
  const [lotCode, setLotCode] = useState('');
  const [quantity, setQuantity] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [guardando, setGuardando] = useState(false);

  async function recargar(): Promise<void> {
    const [{ items: inv }, cat] = await Promise.all([listarInventarioFarmacia(), listarCatalogoFarmacia()]);
    setItems(inv);
    setCatalogo(cat);
  }

  useEffect(() => {
    void recargar();
  }, []);

  async function cargarLote(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setGuardando(true);
    try {
      const r = await cargarLoteFarmacia({
        catalogItemId: Number(catalogItemId),
        lotCode: lotCode.trim(),
        quantity: Number(quantity),
        expiresAt: expiresAt || null,
      });
      if (!r.ok) {
        notify({ color: 'red', message: r.error, autoClose: false });
        return;
      }
      setLotCode('');
      setQuantity('');
      setExpiresAt('');
      await recargar();
    } finally {
      setGuardando(false);
    }
  }

  async function ajustar(lotId: number, delta: number): Promise<void> {
    const r = await ajustarLoteFarmacia({ lotId, quantity: delta, reason: delta > 0 ? 'Ajuste manual (+)' : 'Ajuste manual (-)' });
    if (!r.ok) {
      notify({ color: 'red', message: r.error, autoClose: false });
      return;
    }
    await recargar();
  }

  return (
    <div className="space-y-6">
      <form onSubmit={(e) => void cargarLote(e)} className="grid grid-cols-2 gap-2 rounded-lg border border-border p-4 sm:grid-cols-5">
        <div className="col-span-2">
          <Label className="mb-1 block text-xs">{t('farmaciaApp.inventario.item')}</Label>
          <Select value={catalogItemId} onValueChange={setCatalogItemId}>
            <SelectTrigger>
              <SelectValue placeholder={t('farmaciaApp.inventario.itemPlaceholder')} />
            </SelectTrigger>
            <SelectContent>
              {catalogo.map((item) => (
                <SelectItem key={item.id} value={String(item.id)}>
                  {item.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="mb-1 block text-xs">{t('farmaciaApp.inventario.lote')}</Label>
          <Input value={lotCode} onChange={(e) => setLotCode(e.target.value)} required />
        </div>
        <div>
          <Label className="mb-1 block text-xs">{t('farmaciaApp.inventario.cantidad')}</Label>
          <Input type="number" min="1" value={quantity} onChange={(e) => setQuantity(e.target.value)} required />
        </div>
        <div>
          <Label className="mb-1 block text-xs">{t('farmaciaApp.inventario.vence')}</Label>
          <Input type="date" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} />
        </div>
        <div className="col-span-2 sm:col-span-5">
          <Button type="submit" disabled={guardando || !catalogItemId}>
            {guardando ? t('farmaciaApp.catalogo.guardando') : t('farmaciaApp.inventario.cargar')}
          </Button>
        </div>
      </form>

      <ReponerBotiquin onTransferido={() => void recargar()} />

      {items === undefined ? (
        <p className="text-sm text-hce-muted">{t('farmaciaApp.cargando')}</p>
      ) : items.length === 0 ? (
        <p className="text-sm text-hce-muted">{t('farmaciaApp.inventario.vacio')}</p>
      ) : (
        <div className="space-y-3">
          {items.map((item) => (
            <div key={item.stockItemId} className="rounded-lg border border-border p-3">
              <div className="mb-2 flex items-center justify-between">
                <span className="text-sm font-medium text-hce-ink">{item.name}</span>
                <span className="text-xs text-hce-muted">
                  {t('farmaciaApp.inventario.disponible', { cantidad: item.quantity, unidad: item.unit })}
                </span>
              </div>
              <ul className="space-y-1">
                {item.lots.map((lote) => (
                  <li key={lote.id} className="flex items-center justify-between gap-2 text-xs text-hce-muted">
                    <span>
                      {lote.lotCode}
                      {lote.expiresAt ? ` · ${t('farmaciaApp.inventario.venceEl', { fecha: new Date(lote.expiresAt).toLocaleDateString('es-EC') })}` : ''}
                      {' — '}
                      {lote.quantity}
                    </span>
                    <span className="flex gap-1">
                      <button type="button" onClick={() => void ajustar(lote.id, 1)} className="rounded px-1.5 py-0.5 hover:bg-hce-paper hover:text-hce-ink">
                        +1
                      </button>
                      <button
                        type="button"
                        disabled={lote.quantity <= 0}
                        onClick={() => void ajustar(lote.id, -1)}
                        className="rounded px-1.5 py-0.5 hover:bg-hce-paper hover:text-hce-ink disabled:opacity-40"
                      >
                        -1
                      </button>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
